import { useTranslation } from "react-i18next";
import type { PlannedInput } from "../../../api/content/stepPlans";
import type { PromptPart, PromptParts } from "../../../api/threads/promptPartsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import InputCard from "../../workflows/inputCards/InputCard";
import { plannedCardFacts } from "../../workflows/inputCards/inputCardFacts";
import { useItemFacts } from "../../workflows/inputCards/itemFacts";
import { itemTypeText } from "../../workflows/inputCards/typeWords";
import type { ItemAtVersion } from "../prepare";
import Chip from "../../../ui/feedback/Chip";
import type { ChipTone } from "../../../ui/styles/chipStyles";
import { recordedProblemError } from "../entries/recordedProblems";
import { valueText } from "../status/valueText";
import FilledText, { VALUE_PREVIEW_CLASS } from "./FilledText";
import { shownValue, sourceNote, sourceText, type StepNameOf } from "./planText";
import { partItemReference, promptPartTitle } from "./promptPartWords";
import PlannedValueField from "./PlannedValueField";
import { picksItsValue } from "./valuePicking";
import ValueEditor from "./ValueEditor";

/** Values a person gave here, by the key each is sent under. */
export type GivenValues = Readonly<Record<string, unknown>>;

const SOURCE_TONE: Partial<Record<PlannedInput["source"], ChipTone>> = { missing: "warning", wired_pending: "info" };

interface PlannedInputsListProps<Input extends PlannedInput> {
  inputs: readonly Input[];
  stepName: StepNameOf;
  /** Values given here; `null` where none can be given. */
  values: GivenValues | null;
  onChange: (values: GivenValues) => void;
  /** The key a value for the input is sent under: its name for a step run, a name or a destination at a start. */
  keyOf?: (input: Input) => string;
  /** An agent step's prewritten prompt: shown as the first card (F56). */
  prompt?: string | null;
  /**
   * What the step's prompt fills itself, as its run would fill it now (F148): where the list shows the prompt
   * (`prompt`), it shows it filled, with a card for each part a selector fills; an input part's text opens under its
   * input's card, an editor's too. `null` while unread, or for a step without a prompt.
   */
  parts?: PromptParts | null;
  /**
   * The project whose library an editable `item` input picks from (F38), and whose items the
   * cards show by class, version and origin (F61); none where items are not picked, as at a start.
   */
  pickItemsIn?: string;
  /** The project whose undecided results an editable recorded-result input picks from (A8). */
  pickResultsIn?: string;
  /** Opens the edit Thread on an item picked here (F51); none where nothing is edited. */
  onEditItem?: ((item: ItemAtVersion) => void) | null;
}

/**
 * A step's inputs as cards generated from module B's plan (ADR-0032 D4, D7; Johannes, 27 Oct,
 * point 8; F56): each with its label, whether it is required, its kinds, its value prefilled
 * where it has one, and in plain words where that value comes from, such as "Filled by ‹step›
 * when it runs". An input a value can fill, and that no step or the Thread fills, is an
 * editable field; a change shows as such and resets. Where items are picked, an `item` input
 * takes one chosen with the library picker (F38), of the kinds module B says may fill it; module B
 * plans the reference as given, and the run reads the item it names and checks its classes.
 * A recorded result is picked among those the person may decide, never typed (A8).
 * Required ones first (R-5).
 */
export default function PlannedInputsList<Input extends PlannedInput>({ inputs, stepName, values, onChange, keyOf = (input) => input.name, prompt = null, parts = null, pickItemsIn, pickResultsIn, onEditItem = null }: PlannedInputsListProps<Input>) {
  const { t } = useTranslation();
  // An input an earlier step's output fills shows what that step made (`made`), never its default (Sol c1, C1-01).
  // A part the step reads from the project (Q1 option A) holds the version its prompt read: the one the card shows.
  const readOf = (input: Input): string | null => parts?.parts.find((part) => part.input === input.name)?.referencesRead[0] ?? null;
  const prefilledOf = (input: Input) =>
    input.made !== null ? null : input.source === "project_item" ? readOf(input) : (input.value ?? (input.source === "wired_pending" ? null : input.default));
  // The item an item input holds, given here or prefilled: a value holds only its reference.
  const itemReferenceOf = (input: Input): string | null => {
    const held = values?.[keyOf(input)] ?? prefilledOf(input);
    return input.takesItem && typeof held === "string" ? held : null;
  };
  // The parts a selector fills go with the prompt, in the list that shows it.
  const selectorParts = prompt === null ? [] : (parts?.parts ?? []).filter((part) => part.source === "selector" && part.input === null);
  const items = useItemFacts(pickItemsIn, [...inputs.flatMap((input) => itemReferenceOf(input) ?? []), ...selectorParts.flatMap((part) => partItemReference(part) ?? [])]);
  // The text an input part puts in the prompt, by the input it reads.
  const filledOf = (input: Input): string | null => parts?.parts.find((part) => part.input === input.name)?.text ?? null;
  // An input's part that isn't filled says why under its card (Sol r1 S2): the problem its run would fail with, and, for a
  // part read from the project, whose source otherwise reads as filled, that it isn't filled yet; other sources say their wait.
  const unfilledOf = (input: Input): PromptPart | null => {
    const part = parts?.parts.find((candidate) => candidate.input === input.name) ?? null;
    return part === null || part.text !== null || (part.problem === null && input.source !== "project_item") ? null : part;
  };
  if (inputs.length === 0 && prompt === null && selectorParts.length === 0) return null;
  const ordered = [...inputs.filter((input) => input.required), ...inputs.filter((input) => !input.required)];
  const filledPrompt = parts?.prompt ?? null;
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {prompt === null ? null : (
        <InputCard title={t("threads.plan.prompt.label")} facts={{ type: itemTypeText({ kind: "prompt", many: false }, t), version: null, origin: null, edit: null }} onEdit={null} className="sm:col-span-2">
          <p className={`${VALUE_PREVIEW_CLASS} max-h-48`}>{filledPrompt ?? prompt}</p>
          {filledPrompt === null ? <p className="app-text-muted text-xs">{t("threads.plan.prompt.source")}</p> : null}
        </InputCard>
      )}
      {selectorParts.map((part) => {
        const reference = partItemReference(part);
        const title = promptPartTitle(part, reference === null ? null : (items.get(reference)?.name ?? null), t);
        // Closed, the card says only its name; its text opens below (taste round, 3 Nov).
        return (
          <li key={`part:${part.name}`} className="app-card min-w-0 space-y-1.5 p-2 text-sm">
            <PartText part={part} title={title} />
          </li>
        );
      })}
      {ordered.map((input) => {
        const key = keyOf(input);
        const given = values?.[key];
        const editable = values !== null && input.takesValue && !FILLED_ELSEWHERE.has(input.source);
        // An input its kind's own picker edits states its need there; the list adds no second one.
        const ownEditor = editable && picksItsValue(input, { items: pickItemsIn, results: pickResultsIn });
        const note = given === undefined ? sourceNote(input, t, stepName) : null;
        const prefilled = prefilledOf(input);
        const reference = itemReferenceOf(input);
        const item = reference === null ? null : (items.get(reference) ?? null);
        const unfilled = unfilledOf(input);
        return (
          <InputCard
            key={key}
            title={input.label}
            facts={plannedCardFacts(input, item, t)}
            need={input.required ? t("threads.current.inputs.required") : t("threads.current.inputs.optional")}
            description={input.description}
            onEdit={onEditItem}
          >
            {editable ? (
              <>
                <PlannedValueField
                  input={input}
                  value={given ?? prefilled}
                  onChange={(value) => { onChange({ ...values, [key]: value }); }}
                  pickedIn={{ items: pickItemsIn, results: pickResultsIn }}
                  itemName={item?.name ?? null}
                />
                {/* What the prompt reads of the value given here, where that is more than the field shows: a picked item's text. */}
                {filledOf(input) === (given ?? prefilled) ? null : filledText(item?.name ?? input.label, filledOf(input))}
              </>
            ) : input.made !== null ? (
              // An earlier step's output, read-only, as module C sums it up (WD-4): never an editor's value; it opens to
              // the text the prompt reads from it (F148).
              <Preview summary={valueText(input.made, t)} filled={filledOf(input)} />
            ) : prefilled === null ? (
              filledText(input.label, filledOf(input))
            ) : typeof prefilled === "boolean" ? (
              <ValueEditor label={input.label} accepts={input.accepts} lineages={input.lineages} value={prefilled} disabled={true} onChange={() => undefined} />
            ) : (
              <Preview summary={item === null ? shownValue(prefilled) : item.name} filled={filledOf(input)} />
            )}
            {unfilled === null ? null : <Unfilled part={unfilled} />}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {given === undefined ? (
                ownEditor && input.source === "missing" ? null : <SourceLabel input={input} stepName={stepName} />
              ) : (
                <>
                  <Chip tone="info" size="compact">{t("threads.current.inputs.changed")}</Chip>
                  <button
                    type="button"
                    className="app-link"
                    onClick={() => {
                      const { [key]: _reset, ...rest } = values ?? {};
                      onChange(rest);
                    }}
                  >
                    {t("threads.current.inputs.reset")}
                  </button>
                </>
              )}
            </div>
            {note === null ? null : <p className="app-text-muted text-xs">{note}</p>}
          </InputCard>
        );
      })}
    </ul>
  );
}

/** A value at a glance; where the prompt reads more of it than that, it opens to the whole text (F148). */
function Preview({ summary, filled }: { summary: string; filled: string | null }) {
  return filled === null || filled === summary ? <p className={VALUE_PREVIEW_CLASS}>{summary}</p> : <FilledText summary={summary} text={filled} />;
}

/** What the prompt reads of an input, closed under the name `summary`. */
function filledText(summary: string, text: string | null) {
  return text === null ? null : <FilledText summary={summary} text={text} />;
}

/** A part a selector fills, by its name: its text to open, or why it isn't filled. */
function PartText({ part, title }: { part: PromptPart; title: string }) {
  if (part.text !== null) return <FilledText summary={title} text={part.text} asTitle={true} />;
  return (
    <>
      <span className="font-medium">{title}</span>
      <Unfilled part={part} />
    </>
  );
}

/** Why a part isn't filled: the problem its run would fail with, or that it isn't filled yet. */
function Unfilled({ part }: { part: PromptPart }) {
  const { t } = useTranslation();
  return part.problem === null ? <p className="app-text-muted text-xs">{t("threads.plan.parts.notFilled")}</p> : <ErrorNotice error={recordedProblemError(part.problem)} compact={true} />;
}

/** Sources a run's value would override silently: an earlier step's output, the one a step will give, one an earlier Workflow made (F126), or the Thread's. */
const FILLED_ELSEWHERE: ReadonlySet<PlannedInput["source"]> = new Set(["wired_output", "wired_pending", "carried_output", "context"]);

function SourceLabel({ input, stepName }: { input: PlannedInput; stepName: StepNameOf }) {
  const { t } = useTranslation();
  const text = sourceText(input, t, stepName);
  const tone = SOURCE_TONE[input.source];
  return tone === undefined ? <span className="app-text-muted">{text}</span> : <Chip tone={tone} size="compact">{text}</Chip>;
}
